import React, { useState, useEffect, useRef } from 'react';
import { ClientService } from "../service/ClientService";
import { ModuleService } from "../service/ModuleService";
import { ProfileService } from "../service/ProfileService";
import { LanguageService } from "../service/LanguageService";
import classNames from 'classnames';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { AuthorizationService, MANAGE_CLIENTS } from '../service/AuthorizationService';
import './Crud.scss'
import * as _ from 'lodash';
import { ProgressBar } from 'primereact/progressbar';
import { v4 as uuidv4 } from 'uuid';
import {Chips} from 'primereact/chips';
import { Paginator } from 'primereact/paginator';
import { Fieldset } from 'primereact/fieldset';
import { ToggleButton } from 'primereact/togglebutton';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Password } from 'primereact/password';

export function ClientCrud() {

    let emptyClient = {
        id: uuidv4(),
        name: '',
        logo: '',
        shortcode: '',
        powerbi_username: '',
        powerbi_password: '',
        powerbi_appid: '',
        powerbi_secret: '',
        use_custom_url: false,
        custom_url: '',
        custom_layout: {},
        email_config: {smtp: {}, passwordSet: {}},
        signup_config: {},
        powerbi_config: {},
        modules: [],
        config: {}
    };

    const [clients, setClients] = useState(null);
    const [profiles, setProfiles] = useState([]);
    const [modules, setModules] = useState([]);
    const [clientDialog, setClientDialog] = useState(false);
    const [deleteClientDialog, setDeleteClientDialog] = useState(false);
    const [client, setClient] = useState(emptyClient);
    const [selectedClients, setSelectedClients] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [hasAccess, setHasAccess] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const toast = useRef(null);
    const dt = useRef(null);
    const clientService = new ClientService();
    const moduleService = new ModuleService();
    const profileService = new ProfileService();
    const authorizationService = new AuthorizationService();
    const languageService = new LanguageService();

    const [search, setSearch] = useState('');
    const [sortField, setSortField] = useState('name');
    const [sortOrder, setSortOrder] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [first, setFirst] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const updateList = async () => {
        const data = await clientService.list(first, rowsPerPage, search, sortField, sortOrder);
        setClients(data.items);
        setTotalRecords(data.totalRecords)
    }

    const onPageChange = async (pageEvent) => {
        setFirst(pageEvent.first);
        setRowsPerPage(pageEvent.rows);
        const data = await clientService.list(pageEvent.first, pageEvent.rows, search, sortField, sortOrder);
        setClients(data.items);
        setTotalRecords(data.totalRecords)
    }

    const doSearch = async (reset) => {
        const data = await clientService.list(first, rowsPerPage, reset ? null : globalFilter, sortField, sortOrder);
        setSearch(globalFilter);
        setClients(data.items);
        setTotalRecords(data.totalRecords)
    }

    const onSort = async (sortEvent) => {
        setSortField(sortEvent.sortField);
        setSortOrder(sortEvent.sortOrder);
        const data = await clientService.list(first, rowsPerPage, search, sortEvent.sortField, sortEvent.sortOrder);
        setClients(data.items);
        setTotalRecords(data.totalRecords)
    }

    useEffect(() => {
        setHasAccess(authorizationService.hasPermission(MANAGE_CLIENTS));

        if(authorizationService.hasPermission(MANAGE_CLIENTS)){
            async function fetchData() {
                await updateList();
                const profileList = await profileService.listAll();
                setProfiles(profileList.items);

                const modulesList = await moduleService.listAll();
                setModules(modulesList.items);

                setLoading(false);
            }        
            fetchData();
            
        }
    }, []);

    const openNew = () => {
        emptyClient.id = uuidv4();
        setClient(emptyClient);
        setSubmitted(false);
        setClientDialog(true);
    }

    const hideDialog = () => {
        if(!saving){
            setSubmitted(false);
            setClientDialog(false);
        }
    }

    const hideDeleteClientDialog = () => {
        setDeleteClientDialog(false);
    }

    const saveDisabled = () => {
        return (client.name || "") === ""
            || (client.shortcode || "") === ""
            || (client.logo || "") === "";
    }

    const saveClient = async () => {
        try {
            setSaving(true);
            setSubmitted(true);

            if (client.name.trim()) {
                if (client.id) {
                    await clientService.update(client);
                    toast.current.show({ severity: 'success', summary: languageService.getText('Successful'), detail: languageService.getText('Client_Updated'), life: 3000 });
                }
                else {
                    await clientService.create(client);
                    toast.current.show({ severity: 'success', summary: languageService.getText('Successful'), detail: languageService.getText('Client_Created'), life: 3000 });
                }

                await updateList();
                setClientDialog(false);
                setClient(emptyClient);
                setSaving(false);
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: error.toString(), sticky: true });
            setSaving(false);
            console.log(error);
            throw error;
        }
    }

    const editClient = (client) => {
        console.log(client);
        setClient({ ...client });
        setClientDialog(true);
    }

    const confirmDeleteClient = (client) => {
        setClient(client);
        setDeleteClientDialog(true);
    }

    const deleteClient = async () => {
        try {
            setSaving(true);
            await clientService.delete(client);
            await updateList();
            setDeleteClientDialog(false);
            setClient(emptyClient);
            setSaving(false);
            toast.current.show({ severity: 'success', summary: languageService.getText('Successful'), detail: languageService.getText('Client_Deleted'), life: 3000 });    
        } catch (error) {
            console.log(error);
            throw error;
        }
    }

    const onInputChange = (e, name) => {
        const val = (e.target && e.target.value) || '';
        let _client = { ...client };
        _.set(_client, name, val);
        setClient(_client);
    }

    const onValueChange = (val, name) => {
        console.log(val, name)
        let _client = { ...client };
        _.set(_client, name, val);
        setClient(_client);
    }

    const leftToolbarTemplate = () => {
        return (
            <React.Fragment>
                <Button label={languageService.getText("New")} icon="pi pi-plus" className="p-button-success p-mr-2" onClick={openNew} />
            </React.Fragment>
        )
    }

    const actionBodyTemplate = (rowData) => {
        return (
            <React.Fragment>
                <Button icon="pi pi-pencil" className="p-button-rounded p-button-success p-mr-2" onClick={() => editClient(rowData)} />
                <Button icon="pi pi-trash" className="p-button-rounded p-button-warning" onClick={() => confirmDeleteClient(rowData)} />
            </React.Fragment>
        );
    }

    const header = (
        <div className="table-header">
            <h5 className="p-m-0">{languageService.getText('Manage_Clients')}</h5>
            <span className="p-input-icon-left">
                <i className="pi pi-search" />
                <InputText type="search" onInput={(e) => {setGlobalFilter(e.target.value); if(e.target.value === "") doSearch(true);}} placeholder="Search..."  onKeyDown={(e) => { if (e.key === 'Enter') { doSearch()}}}/>
            </span>
        </div>
    );

    const clientDialogFooter = (
        <React.Fragment>
            { !saving && <Button label={languageService.getText("Cancel")} icon="pi pi-times" className="p-button-text" onClick={hideDialog} />}
            { !saving && <Button disabled={saveDisabled()} label={languageService.getText("Save")} icon="pi pi-check" className="p-button-text" onClick={saveClient} />}
            { saving && <ProgressSpinner style={{width: '20px', height: '20px'}} strokeWidth="8"/>}
        </React.Fragment>
    );
    const deleteClientDialogFooter = (
        <React.Fragment>
            { !saving && <Button label={languageService.getText("No")} icon="pi pi-times" className="p-button-text" onClick={hideDeleteClientDialog} />}
            { !saving && <Button label={languageService.getText("Yes")} icon="pi pi-check" className="p-button-text" onClick={deleteClient} />}
            { saving && <ProgressSpinner style={{width: '20px', height: '20px'}} strokeWidth="8"/>}
        </React.Fragment>
    );

    const addTag = () => {
        if(!client.tags){
            client.tags = [];
        }

        client.tags.push({
            id: uuidv4(),
            name: '',
            client_id: client.id,
            values: []
        });

        setClient(client);
    }

    const removeTag = (tag_id) => {
        client.tags.find(t => t.id === tag_id).deleted = true;
        setClient(Object.assign({}, client));
    }

    const onChangeTagName = (e, id) => {
        client.tags.find(t => t.id === id).name = e.target.value;
        setClient(Object.assign({}, client));
    }

    const addTagValue = (tag_id, name) => {
        client.tags.find(t => t.id === tag_id).values.push({ id: uuidv4(), name, tag_id });
        setClient(Object.assign({}, client));
    }

    const removeTagValue = (tag_id, value) => {
        client.tags.find(t => t.id === tag_id).values.find(v => v.id === value[0].id).deleted = true;
        setClient(Object.assign({}, client));
    }

    if(hasAccess){

        return (
            <div className="datatable-crud-demo">
                <Toast ref={toast} />
                <div className="card">
                    { loading && <ProgressBar mode="indeterminate"/> }
                    { !loading && <Toolbar className="p-mb-4" left={leftToolbarTemplate}></Toolbar>}

                    { !loading &&
                    <>
                    <DataTable ref={dt} value={clients} selection={selectedClients} onSelectionChange={(e) => setSelectedClients(e.value)}
                        dataKey="id"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageClient RowsPerPageDropdown"
                        currentPageClientTemplate="Showing {first} to {last} of {totalRecords} clients"
                        header={header}
                        autoLayout={true}
                        onSort={onSort}
                        sortField={sortField} 
                        sortOrder={sortOrder}
                        >

                        <Column field="bizid" header={languageService.getText("ID")} sortable></Column>
                        <Column field="name" header={languageService.getText("Name")} sortable></Column>
                        <Column field="shortcode" header="Short Code / URL" body={(data) => data.use_custom_url ? data.custom_url : data.shortcode }></Column>
                        <Column style={{width: '150px'}} body={actionBodyTemplate}></Column>
                    </DataTable>

                    <Paginator first={first} rows={rowsPerPage} totalRecords={totalRecords} rowsPerPageOptions={[5, 10, 25, 50, 100]} onPageChange={onPageChange}></Paginator>
                    </>
                    }
                </div>

                { client && <>
                <Dialog visible={clientDialog} style={{ width: '90vw' }} header="Client Details" modal className="p-fluid" footer={clientDialogFooter} onHide={hideDialog}>
                    <div className="p-formgrid p-grid">
                        <div className="p-field p-col-3">
                            <label htmlFor="bizid">{languageService.getText("ID")}*</label>
                            <InputText disabled={saving} id="bizid" value={client.bizid} onChange={(e) => onInputChange(e, 'bizid')} required autoFocus className={classNames({ 'p-invalid': submitted && !client.bizid })} />
                            {submitted && !client.bizid && <small className="p-invalid">{languageService.getText("ID")} {languageService.getText("is_required")}.</small>}
                        </div>
                        <div className="p-field p-col-9">
                            <label htmlFor="name">{languageService.getText("Name")}*</label>
                            <InputText disabled={saving} id="name" value={client.name} onChange={(e) => onInputChange(e, 'name')} required autoFocus className={classNames({ 'p-invalid': submitted && !client.name })} />
                            {submitted && !client.name && <small className="p-invalid">{languageService.getText("Name")} {languageService.getText("is_required")}.</small>}
                        </div>
                    </div>
                    <Fieldset legend={LanguageService.text("URL")} toggleable style={{marginBottom: '5px'}}>
                        <div className="p-formgrid p-grid">
                            <div className="p-field p-col-12 p-lg-2">
                                <label htmlFor="use_custom_url">{LanguageService.text("Use Custom URL")}</label>
                                <ToggleButton onLabel={LanguageService.text("Yes")}  offLabel={LanguageService.text("No")}  onIcon="pi pi-check" offIcon="pi pi-times" checked={client.use_custom_url} onChange={(e) => onValueChange(e.value, 'use_custom_url')} />
                            </div>
                            { client.use_custom_url ? <div className="p-field p-col-12 p-lg-10">
                                <label htmlFor="shortcode">Custom URL*</label>
                                <InputText disabled={saving} id="custom_url" value={client.custom_url} onChange={(e) => onInputChange(e, 'custom_url')} required autoFocus className={classNames({ 'p-invalid': submitted && !client.custom_url })} />
                                {submitted && !client.use_custom_url && !client.custom_url && <small className="p-invalid">Custom URL {languageService.getText("is_required")}.</small>}
                            </div>
                            : <div className="p-field p-col-12 p-lg-10">
                                <label htmlFor="shortcode">{LanguageService.text("Shortcode")}*</label>
                                <InputText disabled={saving} id="shortcode" value={client.shortcode} onChange={(e) => onInputChange(e, 'shortcode')} required autoFocus className={classNames({ 'p-invalid': submitted && !client.shortcode })} />
                                {submitted && !client.use_custom_url && !client.shortcode && <small className="p-invalid">Short Code {languageService.getText("is_required")}.</small>}
                            </div>
                            }
                        </div>
                    </Fieldset>

                    <Fieldset legend={LanguageService.text("Self Sign-Up")} toggleable style={{marginBottom: '5px'}}>
                        <div className="p-formgrid p-grid">
                            <div className="p-field p-col-12 p-lg-2">
                                <label htmlFor="signup_config_allowed">{LanguageService.text("Allowed")}</label>
                                <ToggleButton onLabel={LanguageService.text("Yes")}  offLabel={LanguageService.text("No")}  onIcon="pi pi-check" offIcon="pi pi-times" checked={client.signup_config.allowed} onChange={(e) => onValueChange(e.value, 'signup_config.allowed')} />
                            </div>
                            { client.signup_config.allowed && <div className="p-field p-col-12 p-lg-10">
                                <label htmlFor="signup_defaultProfile">Default Profile</label>
                                <Dropdown disabled={saving} id="signup_defaultProfile"  optionLabel="name" optionValue="id" value={client.signup_config.defaultProfile} options={profiles || []} onChange={(e) => onInputChange(e, 'signup_config.defaultProfile')} placeholder="Select a profile" />
                            </div>}
                        </div>
                    </Fieldset>
                    
                    <Fieldset legend={LanguageService.text("Layout Customizations")} toggleable style={{marginBottom: '5px'}}>
                        <div className="p-formgrid p-grid">
                            <div className="p-field p-col-12 p-lg-6">
                                <label htmlFor="description">Logo URL*</label>
                                <InputText disabled={saving} id="logo" value={client.logo} onChange={(e) => onInputChange(e, 'logo')} required autoFocus className={classNames({ 'p-invalid': submitted && !client.logo })} />
                                {submitted && !client.logo && <small className="p-invalid">Logo URL {languageService.getText("is_required")}.</small>}
                            </div>
                            <div className="p-field p-col-12 p-lg-6">
                                <label htmlFor="description">FavIcon URL*</label>
                                <InputText disabled={saving} id="logo" value={client.custom_layout.favicon || ''} onChange={(e) => onInputChange(e, 'custom_layout.favicon')} required autoFocus />                                
                            </div>
                            <div className="p-field p-col-12 p-lg-6">
                                    <label htmlFor="description">URL Landing Page Customizada</label>
                                </div>
                                <div className="p-field p-col-12 p-lg-6">&nbsp;</div>

                                <div className="p-field p-col-12 p-lg-6">
                                <InputText disabled={saving} id="logo" value={(client.config.custom_initial_page || {}).url || ''} onChange={(e) => onInputChange(e, 'config.custom_initial_page.url')} required autoFocus  style={{width: '100%'}}/>  
                                </div>
                                <div className="p-field p-col-12 p-lg-6">&nbsp;</div>
                            <div className="p-field p-col-12 p-lg-3">
                                <label htmlFor="primaryBackgroundColor">Primary Background Color</label>
                                <InputText disabled={saving} id="primaryBackgroundColor" value={client.custom_layout.primaryBackgroundColor || ''} onChange={(e) => onInputChange(e, 'custom_layout.primaryBackgroundColor')} required autoFocus style={{backgroundColor: client.custom_layout.primaryBackgroundColor}} />    
                            </div>
                            <div className="p-field p-col-12 p-lg-3">
                                <label htmlFor="secondaryBackgroundColor">Secondary Background Color</label>
                                <InputText disabled={saving} id="secondaryBackgroundColor" value={client.custom_layout.secondaryBackgroundColor || ''} onChange={(e) => onInputChange(e, 'custom_layout.secondaryBackgroundColor')} required autoFocus style={{backgroundColor: client.custom_layout.secondaryBackgroundColor}} />    
                            </div>
                            <div className="p-field p-col-12 p-lg-3">
                                <label htmlFor="primaryButtonColor">Primary Button Color</label>
                                <InputText disabled={saving} id="primaryButtonColor" value={client.custom_layout.primaryButtonColor || ''} onChange={(e) => onInputChange(e, 'custom_layout.primaryButtonColor')} required autoFocus style={{backgroundColor: client.custom_layout.primaryButtonColor}} />    
                            </div>
                            <div className="p-field p-col-12 p-lg-3">
                                <label htmlFor="primaryFontColor">Primary Font Color</label>
                                <InputText disabled={saving} id="primaryFontColor" value={client.custom_layout.primaryFontColor || ''} onChange={(e) => onInputChange(e, 'custom_layout.primaryFontColor')} required autoFocus style={{backgroundColor: client.custom_layout.primaryFontColor}} />    
                            </div>
                        </div>
                    </Fieldset> 

                    <Fieldset legend={LanguageService.text("SMTP Configuration")} toggleable style={{marginBottom: '5px'}}>
                        <div className="p-formgrid p-grid">
                            <div className="p-field p-col-12 p-lg-6">
                                <label htmlFor="description">URL*</label>
                                <InputText disabled={saving} id="smtp_url" value={client.email_config.smtp.url} onChange={(e) => onInputChange(e, 'email_config.smtp.url')} />
                            </div>
                            <div className="p-field p-col-12 p-lg-6">
                                <label htmlFor="description">Porta*</label>
                                <InputText disabled={saving} id="smtp_port" value={client.email_config.smtp.port} onChange={(e) => onInputChange(e, 'email_config.smtp.port')} />
                            </div>
                            <div className="p-field p-col-12 p-lg-6">
                                <label htmlFor="description">Login*</label>
                                <InputText disabled={saving} id="smtp_user" value={client.email_config.smtp.user} onChange={(e) => onInputChange(e, 'email_config.smtp.user')} />
                            </div>
                            <div className="p-field p-col-12 p-lg-6">
                                <label htmlFor="description">Senha*</label>
                                <Password feedback={false} toggleMask={true}  disabled={saving} id="smtp_password" value={client.email_config.smtp.password} onChange={(e) => onInputChange(e, 'email_config.smtp.password')} />
                            </div>
                        </div>
                    </Fieldset> 

                    <Fieldset legend={LanguageService.text("Messages Configuration")} toggleable style={{marginBottom: '5px'}}>
                        <div className="p-formgrid p-grid">
                            <div className="p-field p-col-12 p-lg-12">
                                <label htmlFor="description"><b>First Password Set Message</b></label>
                            </div>
                            <div className="p-field p-col-12 p-lg-6">
                                <label htmlFor="passwordSet_from">From*</label>
                                <InputText disabled={saving} id="passwordSet_from" value={client.email_config.fromAddress} onChange={(e) => onInputChange(e, 'email_config.fromAddress')} />
                            </div>
                            <div className="p-field p-col-12 p-lg-6">
                                <label htmlFor="passwordSet_subject">Subject*</label>
                                <InputText disabled={saving} id="passwordSet_subject" value={client.email_config.passwordSet.subject} onChange={(e) => onInputChange(e, 'email_config.passwordSet.subject')} />
                            </div>
                            <div className="p-field p-col-12 p-lg-12">
                                <label htmlFor="passwordSet_html">HTML*</label>
                                <InputTextarea disabled={saving} id="passwordSet_html" value={client.email_config.passwordSet.html} onChange={(e) => onInputChange(e, 'email_config.passwordSet.html')} />
                            </div>
                        </div>
                    </Fieldset> 

                    <Fieldset legend={LanguageService.text("Tags")} toggleable style={{marginBottom: '5px'}}>
                        <div className="p-formgrid p-grid">
                        <DataTable 
                            ref={dt} 
                            value={(client.tags || []).filter(t => !t.deleted)}
                            dataKey="id"
                            footer={<Button icon="pi pi-plus" className="p-button-rounded p-button-success p-mr-2" onClick={() => addTag()} />}>

                            <Column field="name" header="Name" body={(rowData) => 
                                <>
                                <InputText value={rowData.name} onChange={(e) => onChangeTagName(e, rowData.id)}/>
                                </>
                            }></Column>
                            <Column field="values" header="Values" body={(rowData) => 
                                <Chips 
                                    value={rowData.values.filter(v => !v.deleted)}
                                    onAdd={(e) => addTagValue(rowData.id, e.value)} 
                                    onRemove={(e) => removeTagValue(rowData.id, e.value)} 
                                    itemTemplate={(item) => item.name}
                                />
                            }></Column>
                            <Column field="id" header=""  style={{width:'100px'}} body={(rowData) => 
                                <Button icon="pi pi-trash" className="p-button-rounded p-button-warning" onClick={() => removeTag(rowData.id)} />
                            }/>
                        </DataTable>
                        </div>
                    </Fieldset>

                    <Fieldset legend={LanguageService.text("Modules")} toggleable style={{marginBottom: '5px'}}>
                        { modules.map(module => 
                            <div className="p-formgrid p-grid">
                                <div className="p-field p-col-3"><label htmlFor={`module_${module.id}`}>{module.name}:</label></div>
                                <div className="p-field p-col-3">
                                    <ToggleButton 
                                        checked={client.modules.filter(m => m.id === module.id).length > 0} 
                                        onChange={(e) => setClient(Object.assign(client,e.value ? { modules: client.modules.concat(module) } : { modules: client.modules.filter(m => m.id !== module.id)}))}
                                    />
                                    </div>
                            </div>
                        )}
                    </Fieldset>

                    { (client.modules.filter(m => m.id === "REPORTS").length > 0) && <>
                    <Fieldset legend={LanguageService.text("Power BI Info")} toggleable style={{marginBottom: '5px'}}>
                        <div className="p-formgrid p-grid">
                            <div className="p-field p-col">
                                <label htmlFor="clientId">Username*</label>
                                <InputText disabled={saving} id="clientId" value={client.powerbi_username} onChange={(e) => onInputChange(e, 'powerbi_username')} required className={classNames({ 'p-invalid': submitted && !client.powerbi_username })} />
                                {submitted && !client.powerbi_username && <small className="p-invalid">Username {languageService.getText("is_required")}.</small>}
                            </div>
                            <div className="p-field p-col">
                                <label htmlFor="groupId">Password*</label>
                                <Password feedback={false} toggleMask={true}  disabled={saving} id="groupId" value={client.powerbi_password} onChange={(e) => onInputChange(e, 'powerbi_password')} required className={classNames({ 'p-invalid': submitted && !client.powerbi_password })} />
                                {submitted && !client.powerbi_password && <small className="p-invalid">Password {languageService.getText("is_required")}.</small>}
                            </div>
                        </div>
                        <div className="p-formgrid p-grid">
                            <div className="p-field p-col">
                                <label htmlFor="powerbi_appid">App ID*</label>
                                <InputText disabled={saving} id="powerbi_appid" value={client.powerbi_appid} onChange={(e) => onInputChange(e, 'powerbi_appid')} required className={classNames({ 'p-invalid': submitted && !client.powerbi_appid })} />
                                {submitted && !client.powerbi_appid && <small className="p-invalid">App ID {languageService.getText("is_required")}.</small>}
                            </div>
                            <div className="p-field p-col">
                                <label htmlFor="powerbi_secret">Secret*</label>
                                <Password feedback={false} toggleMask={true}  disabled={saving} id="powerbi_secret" value={client.powerbi_secret} onChange={(e) => onInputChange(e, 'powerbi_secret')} required className={classNames({ 'p-invalid': submitted && !client.powerbi_secret })} />
                                {submitted && !client.powerbi_secret && <small className="p-invalid">Secret {languageService.getText("is_required")}.</small>}
                            </div>
                            <div className="p-field p-col">
                                <label htmlFor="powerbi_capacity">Capacity ID</label>
                                <InputText disabled={saving} id="powerbi_capacity" value={client.powerbi_config.capacity} onChange={(e) => onInputChange(e, 'powerbi_config.capacity')} style={{width: '100%'}} />
                            </div>
                        </div>
                    </Fieldset>
                    </>}

                </Dialog></>}

                <Dialog visible={deleteClientDialog} style={{ width: '450px' }} header="Confirm" modal footer={deleteClientDialogFooter} onHide={hideDeleteClientDialog}>
                    <div className="confirmation-content">
                        <i className="pi pi-exclamation-triangle p-mr-3" style={{ fontSize: '2rem' }} />
                        {client && <span>Are you sure you want to delete <b>{client.name}</b>?</span>}
                    </div>
                </Dialog>
            </div>
        );
    }
    else {
        return (
            <div className="datatable-crud-demo">
                <div className="card">
                    <b>Permission Denied</b>
                </div>
            </div>
        );
    }
}